// Copyright 2023 Sorama B.V.

import { autoinject, bindable, customElement } from "aurelia-framework";
import { ObjectExtensions } from "extensions/ObjectExtensions";

@customElement("buy-button")
@autoinject
export class BuyButton {
    @bindable
    public productId: string;
    @bindable
    public variantId: string;
    @bindable
    customStyle: object;
    @bindable
    buttonText: string;
    @bindable
    disabled: boolean;
    @bindable
    showPrice: boolean;

    protected buyButtonRef: HTMLElement;

    private scriptURL = "https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js";
    private isAttached = false;
    private isInitialized = false;

    constructor() {}

    public attached(): void {
        this.isAttached = true;
        if (this.productId) {
            this.initializeShopify();
        }
    }
    public detached(): void {
        if (this.buyButtonRef) {
            this.buyButtonRef.innerHTML = "";
        }
        this.isAttached = false;
        this.isInitialized = false;
    }

    private initializeShopify(): void {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        if ((window as any).ShopifyBuy?.UI) {
            this.CreateButton();
        } else {
            this.loadScript();
        }
    }

    private loadScript(): void {
        const script = document.createElement("script");
        script.async = true;
        script.src = this.scriptURL;
        (document.getElementsByTagName("head")[0] || document.getElementsByTagName("body")[0]).appendChild(script);
        script.onload = (): void => {
            this.CreateButton();
        };
    }

    private recreateButton(): void {
        // Clean up the old button before creating a new one
        if (this.buyButtonRef) {
            this.buyButtonRef.innerHTML = "";
        }
        this.CreateButton();
    }

    public variantIdChanged(newValue: string, oldValue: string): void {
        // Only re-create the button if the component is already in the DOM and the ID has actually changed.
        if (this.isAttached && newValue !== oldValue) {
            this.isInitialized = false;
            this.recreateButton();
        }
    }

    // private deepMerge(target, source) {
    //     for (const key in source) {
    //         if (source[key] instanceof Object && key in target) {
    //             Object.assign(source[key], this.deepMerge(target[key], source[key]));
    //         }
    //     }
    //     Object.assign(target || {}, source);
    //     return target;
    // }
    private getStyles(): object {
        const defaultStyle = {
            product: {
                "text-align": "left",
            },
            button: {
                "font-family": "Satoshi, sans-serif",
                "font-size": "16px",
                padding: "16px 16px",
                ":hover": {
                    "background-color": "#ce7120",
                },
                "background-color": "#e57e24",
                ":focus": {
                    "background-color": "#ce7120",
                },
            },
            price: {
                "font-family": "Satoshi, sans-serif",
                "font-size": "16px",
                color: "#e57e24",
            },
        };
        // The custom styles will overwrite any matching default values
        const combinedButtonStyle = ObjectExtensions.deepMerge(defaultStyle, this.customStyle || {});

        // If disabled, only override button styles
        if (this.disabled) {
            combinedButtonStyle.button = {
                ...combinedButtonStyle.button,
                "background-color": "rgba(255, 255, 255, 0.1)",
                color: "#bac6d2",
                "pointer-events": "none",
            };
        }
        return combinedButtonStyle;
    }

    private async CreateButton(): Promise<void> {
        if (this.isInitialized) return;
        this.isInitialized = true;

        /* eslint-disable @typescript-eslint/no-explicit-any */
        const ShopifyBuy = (window as any).ShopifyBuy;
        const client = ShopifyBuy.buildClient({
            domain: "sorama-eu.myshopify.com",
            storefrontAccessToken: "eda22c056c913ae4812d19283e29f4f2",
        });

        const ui = await ShopifyBuy.UI.onReady(client);

        ui.createComponent("product", {
            id: this.productId,
            variantId: this.variantId,
            node: this.buyButtonRef,
            moneyFormat: "%E2%82%AC%7B%7Bamount_no_decimals%7D%7D",
            /* eslint-disable  @typescript-eslint/naming-convention */
            /* Because the fields in this object represent css property names which do not adhere to ts/js naming conventions*/
            options: {
                product: {
                    styles: this.getStyles(),
                    contents: {
                        options: false,
                        price: this.showPrice,
                        title: false,
                    },
                    text: {
                        button: this.buttonText,
                    },
                    googleFonts: ["Satoshi"],
                },
                productSet: {
                    styles: {
                        products: {
                            "@media (min-width: 601px)": {
                                "margin-left": "-20px",
                            },
                        },
                    },
                },
                modalProduct: {
                    contents: {
                        img: false,
                        imgWithCarousel: true,
                        button: false,
                        buttonWithQuantity: true,
                    },
                    styles: {
                        product: {
                            "@media (min-width: 601px)": {
                                "max-width": "100%",
                                "margin-left": "0px",
                                "margin-bottom": "0px",
                            },
                        },
                        button: {
                            "font-family": "Open Sans, sans-serif",
                            "font-size": "16px",
                            "padding-top": "16px",
                            "padding-bottom": "16px",
                            ":hover": {
                                "background-color": "#F5A053",
                            },
                            "background-color": "#F5A053",
                            ":focus": {
                                "background-color": "#F5A053",
                            },
                            "border-radius": "0px",
                        },
                        quantityInput: {
                            "font-size": "16px",
                            "padding-top": "16px",
                            "padding-bottom": "16px",
                        },
                    },
                    googleFonts: ["Open Sans"],
                    text: {
                        button: "Add to cart",
                    },
                },
                option: {
                    styles: {
                        label: {
                            "font-family": "Open Sans, sans-serif",
                        },
                        select: {
                            "font-family": "Open Sans, sans-serif",
                        },
                    },
                    googleFonts: ["Open Sans"],
                },
                cart: {
                    styles: {
                        button: {
                            "font-family": "Open Sans, sans-serif",
                            "font-size": "16px",
                            "padding-top": "16px",
                            "padding-bottom": "16px",
                            ":hover": {
                                "background-color": "#F5A053",
                            },
                            "background-color": "#F5A053",
                            ":focus": {
                                "background-color": "#F5A053",
                            },
                            "border-radius": "0px",
                        },
                    },
                    text: {
                        total: "Subtotal",
                        button: "Checkout",
                    },
                    googleFonts: ["Open Sans"],
                },
                toggle: {
                    styles: {
                        toggle: {
                            "font-family": "Open Sans, sans-serif",
                            "background-color": "#F5A053",
                            ":hover": {
                                "background-color": "#F5A053",
                            },
                            ":focus": {
                                "background-color": "#F5A053",
                            },
                        },
                        count: {
                            "font-size": "16px",
                        },
                    },
                    googleFonts: ["Open Sans"],
                },
            },
            /* eslint-enable  @typescript-eslint/naming-convention */
        });
    }
}
